import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { PackageCheckIcon } from 'lucide-react';
export function ReceiveStock() {
  const {
    medicines,
    suppliers,
    purchaseOrders,
    receiveStock
  } = useData();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    medicineId: '',
    supplierId: '',
    purchaseOrderId: '',
    quantity: '',
    batchNumber: '',
    expiryDate: '',
    costPrice: '',
    notes: ''
  });
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setMessage('');
    setError('');
    const result = receiveStock({
      medicineId: form.medicineId,
      supplierId: form.supplierId || undefined,
      purchaseOrderId: form.purchaseOrderId || undefined,
      quantity: Number(form.quantity),
      batchNumber: form.batchNumber,
      expiryDate: form.expiryDate,
      costPrice: Number(form.costPrice),
      notes: form.notes
    });
    if (!result.ok) {
      setError(result.error || 'Unable to receive stock.');
      return;
    }
    setMessage('Stock received. Inventory, batch availability, and movement history were updated.');
    setForm({
      medicineId: '',
      supplierId: '',
      purchaseOrderId: '',
      quantity: '',
      batchNumber: '',
      expiryDate: '',
      costPrice: '',
      notes: ''
    });
  };
  const activeOrders = purchaseOrders.filter(order => ['Pending', 'Partially Received'].includes(order.status));
  const selectedOrder = activeOrders.find(order => order.id === form.purchaseOrderId);
  const availableMedicines = selectedOrder ? medicines.filter(medicine => selectedOrder.lines.some(line => line.medicineId === medicine.id && line.quantityReceived < line.quantityOrdered)) : medicines.filter(medicine => medicine.status === 'Active');
  return <div className="max-w-3xl space-y-5" data-id="element-4557">
      <div data-id="element-4558">
        <h2 className="text-xl font-bold text-slate-950" data-id="element-4559">Receive New Stock</h2>
        <p className="mt-1 text-sm text-slate-500" data-id="element-4560">
          Every receipt creates a batch and receipt movement automatically.
        </p>
      </div>
      <form onSubmit={submit} className="space-y-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-4561">
        <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-id="element-4562">
          <strong data-id="element-4563">Stock control:</strong> received quantity is added to on-hand
          stock immediately and tracked by batch expiry.
        </div>
        {message && <p role="status" className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700" data-id="element-4564">
            {message}
          </p>}
        {error && <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-id="element-4565">
            {error}
          </p>}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-4566">
          <Select label="Medicine" required value={form.medicineId} onChange={e => setForm({
          ...form,
          medicineId: e.target.value
        })} options={availableMedicines.map(item => ({
          value: item.id,
          label: `${item.name} (${item.sku})`
        }))} data-id="element-4567" />
          <Select label={selectedOrder ? 'Supplier (from purchase order)' : 'Supplier (optional)'} value={form.supplierId} disabled={Boolean(selectedOrder)} onChange={e => setForm({
          ...form,
          supplierId: e.target.value
        })} options={[{
          value: '',
          label: 'No supplier selected'
        }, ...suppliers.filter(item => item.status === 'Active').map(item => ({
          value: item.id,
          label: item.name
        }))]} data-id="element-4568" />
          <Select label="Purchase order (optional)" value={form.purchaseOrderId} onChange={e => {
          const order = activeOrders.find(item => item.id === e.target.value);
          setForm({
            ...form,
            purchaseOrderId: e.target.value,
            supplierId: order?.supplierId || form.supplierId,
            medicineId: ''
          });
        }} options={[{
          value: '',
          label: 'Independent receipt'
        }, ...activeOrders.map(item => ({
          value: item.id,
          label: `${item.orderNumber} · ${item.supplierName}`
        }))]} data-id="element-4569" />
          <Input label="Quantity received" required type="number" min="1" value={form.quantity} onChange={e => setForm({
          ...form,
          quantity: e.target.value
        })} data-id="element-4570" />
          <Input label="Batch number" required value={form.batchNumber} onChange={e => setForm({
          ...form,
          batchNumber: e.target.value
        })} data-id="element-4571" />
          <Input label="Expiry date" required type="date" value={form.expiryDate} onChange={e => setForm({
          ...form,
          expiryDate: e.target.value
        })} data-id="element-4572" />
          <Input label="Unit cost (ZMW)" required type="number" min="0" step="0.01" value={form.costPrice} onChange={e => setForm({
          ...form,
          costPrice: e.target.value
        })} data-id="element-4573" />
        </div>
        <label className="block text-sm font-medium text-gray-700" data-id="element-4574">
          Receipt notes{' '}
          <textarea value={form.notes} onChange={e => setForm({
          ...form,
          notes: e.target.value
        })} rows={3} className="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" placeholder="Delivery note, invoice reference, or receiving remarks" data-id="element-4575" />
        </label>
        <div className="flex justify-end" data-id="element-4576">
          <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-id="element-4577">
            <PackageCheckIcon className="mr-2 h-4 w-4" data-id="element-4578" /> Receive stock
          </Button>
        </div>
      </form>
    </div>;
}